import { useCallback, useEffect, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { api } from '../api/client';

function fmtBytes(n) {
  if (!n && n !== 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
function fmtWhen(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Backups() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const poll = useRef(null);

  const load = useCallback(async () => {
    try { const d = await api('/backup'); setData(d); return d; }
    catch { setError('Could not load backups.'); return null; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); return () => { if (poll.current) clearInterval(poll.current); }; }, [load]);

  // Poll while a backup is running.
  useEffect(() => {
    if (data?.running && !poll.current) {
      poll.current = setInterval(async () => {
        const d = await load();
        if (d && !d.running) { clearInterval(poll.current); poll.current = null; setBusy(false); setNotice('Backup complete.'); }
      }, 3000);
    }
    return undefined;
  }, [data?.running, load]);

  async function runNow() {
    setBusy(true); setError(null); setNotice(null);
    try {
      await api('/backup/run', { method: 'POST' });
      setNotice('Backup started…');
    } catch (e) {
      setError(e?.detail || 'Could not start the backup.');
    } finally {
      setBusy(false);
      await load();
    }
  }

  async function remove(name) {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    setError(null); setNotice(null);
    try { await api(`/backup/${encodeURIComponent(name)}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(e?.detail || 'Could not delete that backup.'); }
  }

  async function removeRemote(key, name) {
    if (!window.confirm(`Delete offsite copy ${name}? This cannot be undone.`)) return;
    setError(null); setNotice(null);
    try { await api(`/backup/remote?key=${encodeURIComponent(key)}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(e?.detail || 'Could not delete that offsite backup.'); }
  }

  const last = data?.lastRun;
  const running = !!data?.running;
  const psSnippet = data && [
    '$token = "<your BACKUP_RUN_TOKEN or BILLING_RUN_TOKEN>"',
    `$cmd   = "Invoke-RestMethod -Method Post -Uri ${window.location.origin}/api/backup-run -Headers @{ 'X-Billing-Token' = '$token' }"`,
    '$action  = New-ScheduledTaskAction -Execute "powershell.exe" -Argument "-NoProfile -Command $cmd"',
    '$trigger = New-ScheduledTaskTrigger -Daily -At 2:00AM',
    'Register-ScheduledTask -TaskName "Flightdeck Backup" -Action $action -Trigger $trigger -User "SYSTEM" -RunLevel Highest',
  ].join('\n');

  return (
    <>
      <PageHeader title="Backups" subtitle="Database + application files" />

      <section className="panel panel--pad" style={{ maxWidth: 760 }}>
        {loading ? <div className="hint">Loading…</div> : (
          <>
            <p className="hint" style={{ marginTop: 0 }}>
              Each backup is a single zip containing a <code>pg_dump</code> of the database plus the deployed
              application files (<code>.env</code> and <code>data/</code> included; <code>node_modules</code> and
              build output excluded). Backups are stored on the server and the newest {data?.keep} are kept.
            </p>

            <div className="bk-meta">
              <div><span className="bk-meta__k">Location</span><code className="endpoint">{data?.dir}</code></div>
              <div><span className="bk-meta__k">Retention</span> {data?.keep} most recent</div>
              <div><span className="bk-meta__k">pg_dump</span> <code>{data?.pg_dump}</code></div>
              <div>
                <span className="bk-meta__k">Last run</span>
                {!last ? <span className="badge badge--slate">never</span> : last.ok
                  ? <span className="badge badge--green">{fmtWhen(last.at)} · {fmtBytes(last.size_bytes)}{last.trigger ? ` · ${last.trigger}` : ''}</span>
                  : <span className="badge badge--red">failed · {fmtWhen(last.at)}</span>}
                {last && last.ok && data?.s3?.enabled && (last.uploaded
                  ? <span className="badge badge--green" style={{ marginLeft: 6 }}>offsite ✓</span>
                  : <span className="badge badge--red" style={{ marginLeft: 6 }}>offsite failed</span>)}
              </div>
              {last && !last.ok && last.error && <div className="form-error" style={{ marginTop: 4 }}>{last.error}</div>}
              {last && last.ok && last.upload_error && <div className="form-error" style={{ marginTop: 4 }}>Offsite upload: {last.upload_error}</div>}
              {data?.s3?.enabled && (
                <div><span className="bk-meta__k">Offsite</span><code className="endpoint">{data.s3.bucket}{data.s3.prefix ? `/${data.s3.prefix}` : ''}</code> <span className="hint">via {data.s3.endpoint}</span></div>
              )}
            </div>

            <div style={{ margin: '14px 0' }}>
              <button className="btn btn--primary" onClick={runNow} disabled={busy || running}>
                {running ? 'Backup running…' : (busy ? 'Starting…' : 'Run backup now')}
              </button>
            </div>

            {error && <div className="form-error">{error}</div>}
            {notice && <div className="notice" style={{ marginTop: 12 }}>{notice}<button className="notice__x" onClick={() => setNotice(null)}>×</button></div>}

            <h3 className="form-section" style={{ marginTop: 18 }}>Stored backups</h3>
            {(!data?.backups || data.backups.length === 0)
              ? <div className="hint">No backups yet.</div>
              : (
                <table className="bk-table">
                  <thead><tr><th>File</th><th>Created</th><th>Size</th><th /></tr></thead>
                  <tbody>
                    {data.backups.map((b) => (
                      <tr key={b.name}>
                        <td className="bk-table__file">{b.name}</td>
                        <td>{fmtWhen(b.created_at)}</td>
                        <td>{fmtBytes(b.size_bytes)}</td>
                        <td className="bk-table__act">
                          <a className="btn btn--sm" href={`/api/backup/${encodeURIComponent(b.name)}/download`}>Download</a>
                          <button className="btn btn--sm btn--ghost" onClick={() => remove(b.name)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

            {data?.s3?.enabled && (
              <>
                <h3 className="form-section" style={{ marginTop: 22 }}>Offsite copies (Wasabi)</h3>
                {data.remote_error && <div className="form-error">Could not list offsite backups: {data.remote_error}</div>}
                {(!data.remote || data.remote.length === 0)
                  ? <div className="hint">No offsite backups yet.</div>
                  : (
                    <table className="bk-table">
                      <thead><tr><th>File</th><th>Created</th><th>Size</th><th /></tr></thead>
                      <tbody>
                        {data.remote.map((b) => (
                          <tr key={b.key}>
                            <td className="bk-table__file">{b.name}</td>
                            <td>{fmtWhen(b.created_at)}</td>
                            <td>{fmtBytes(b.size_bytes)}</td>
                            <td className="bk-table__act">
                              <a className="btn btn--sm" href={`/api/backup/remote/download?key=${encodeURIComponent(b.key)}`}>Download</a>
                              <button className="btn btn--sm btn--ghost" onClick={() => removeRemote(b.key, b.name)}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </>
            )}

            <h3 className="form-section" style={{ marginTop: 22 }}>Scheduled backups</h3>
            <p className="hint" style={{ marginTop: 0 }}>
              {data?.scheduled_enabled
                ? 'A scheduled-task token is configured. Create a daily Windows task that POSTs to the endpoint below (run once in an elevated PowerShell):'
                : 'Set BACKUP_RUN_TOKEN (or reuse BILLING_RUN_TOKEN) in the backend .env to enable the scheduled trigger, then create a daily Windows task that POSTs to:'}
              <br /><code className="endpoint">{`${window.location.origin}/api/backup-run`}</code>
            </p>
            <pre className="bk-pre">{psSnippet}</pre>
          </>
        )}
      </section>
    </>
  );
}
